import React, { useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAddPeserta } from '@/hooks/useKegiatan'
import pb from '@/lib/pocketbase'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import Select from '@/components/ui/Select'
import Card from '@/components/ui/Card'

const pesertaSchema = z.object({
  nama: z.string().min(1, 'Nama harus diisi'),
  nbm: z.string().optional(),
  email: z.string().email('Email tidak valid').or(z.literal('')).optional(),
  telepon: z.string().optional(),
  daerah_id: z.string().optional(),
  pimpinan_daerah_id: z.string().optional(),
  jabatan_id: z.string().optional(),
  status_pembayaran: z.enum(['belum_bayar', 'menunggu_verifikasi', 'lunas', 'batal']),
})

type PesertaFormData = z.infer<typeof pesertaSchema>

const paymentOptions = [
  { label: 'Belum Bayar', value: 'belum_bayar' },
  { label: 'Menunggu Verifikasi', value: 'menunggu_verifikasi' },
  { label: 'Lunas', value: 'lunas' },
  { label: 'Batal', value: 'batal' },
]

export default function PesertaFormPage() {
  const { id: kegiatanId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const addMutation = useAddPeserta()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PesertaFormData>({
    resolver: zodResolver(pesertaSchema),
    defaultValues: {
      status_pembayaran: 'belum_bayar',
    },
  })

  const onSubmit = async (data: PesertaFormData) => {
    if (!kegiatanId) return
    try {
      await addMutation.mutateAsync({
        kegiatan: kegiatanId,
        nama: data.nama,
        nbm: data.nbm || undefined,
        email: data.email || undefined,
        telepon: data.telepon || undefined,
        daerah_id: data.daerah_id || undefined,
        pimpinan_daerah_id: data.pimpinan_daerah_id || undefined,
        jabatan_id: data.jabatan_id || undefined,
        status_pendaftaran: 'diterima',
        status_pembayaran: data.status_pembayaran,
        created_by: pb.authStore.model?.id || '',
      })
      navigate(`/kegiatan/${kegiatanId}`)
    } catch (err) {
      console.error('Add peserta error:', err)
    }
  }

  return (
    <div className="py-4 space-y-4">
      <Card>
        <h2 className="text-base font-bold mb-4">Tambah Peserta</h2>

        <form className="space-y-4">
          <Input
            label="Nama Lengkap *"
            placeholder="Nama peserta"
            error={errors.nama?.message}
            {...register('nama')}
          />

          <Input
            label="NBM"
            placeholder="Nomor Buruh Masjid"
            {...register('nbm')}
          />

          <Input
            label="Email"
            type="email"
            placeholder="email@contoh.com"
            error={errors.email?.message}
            {...register('email')}
          />

          <Input
            label="No. Telepon"
            type="tel"
            placeholder="08xxxxxxxxxx"
            {...register('telepon')}
          />

          <Input
            label="Daerah (PDA)"
            placeholder="Contoh: PDA Surabaya"
            {...register('daerah_id')}
          />

          <Input
            label="Pimpinan Daerah"
            placeholder="Nama pimpinan daerah"
            {...register('pimpinan_daerah_id')}
          />

          <Input
            label="Jabatan"
            placeholder="Contoh: Ketua"
            {...register('jabatan_id')}
          />

          <Select
            label="Status Pembayaran"
            options={paymentOptions}
            {...register('status_pembayaran')}
          />

          <div className="pt-2 space-y-2">
            <Button
              type="submit"
              block
              loading={addMutation.isPending}
              onClick={handleSubmit(onSubmit)}
            >
              Simpan Peserta
            </Button>
            <Button
              type="button"
              variant="outline"
              block
              onClick={() => navigate(`/kegiatan/${kegiatanId}`)}
            >
              Batal
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
